import { useState } from 'react';
import {
  Table, Button, Input, DatePicker, Tooltip,
  Tag, message, Typography, Progress
} from 'antd';
import {
  TrophyOutlined, TeamOutlined, DownloadOutlined,
  SaveOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import { DUMMY_SCORES } from '../dummyData';

const { RangePicker } = DatePicker;
const THEME = '#008385';

// ── Score badge colour — thresholds based on converted count ──────────────────
const getScoreCls = (n) => n >= 15 ? 'excellent' : n >= 10 ? 'good' : n >= 5 ? 'average' : 'poor';
const getRankCls  = (i) => i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : 'other';
const getRankLabel = (i) => i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`;

// ── KPI Summary Row ───────────────────────────────────────────────────────────
const ScoreSummary = ({ scores }) => {
  if (!scores.length) return null;

  const totalAssigned  = scores.reduce((a, s) => a + s.assigned_count, 0);
  const totalConverted = scores.reduce((a, s) => a + (s.converted_count ?? 0), 0);
  const avgConverted   = scores.length > 0
    ? (totalConverted / scores.length).toFixed(1)
    : 0;
  const convPct = totalAssigned > 0
    ? ((totalConverted / totalAssigned) * 100).toFixed(0)
    : 0;
  const top = scores[0]; // already sorted by converted_count desc

  return (
    <div className="aj-stats-row" style={{ marginBottom: 16 }}>
      {[
        { label: 'Total CSRs',      value: scores.length,                      sub: 'active this period',     cls: 'total' },
        { label: 'Avg Converted',   value: avgConverted,                        sub: 'average per CSR',        cls: '' },
        { label: 'Top Performer',   value: top?.name?.split(' ')[0] || '—',    sub: `${top?.converted_count ?? 0} converted`, cls: 'contacted' },
        { label: 'Total Assigned',  value: totalAssigned,                       sub: 'across all CSRs',        cls: 'in-progress' },
        { label: 'Total Converted', value: totalConverted,                      sub: `${convPct}% conversion`, cls: 'converted' },
      ].map(c => (
        <div key={c.label} className={`aj-stat-card ${c.cls}`}>
          <div className="aj-stat-label">{c.label}</div>
          <div className="aj-stat-value">{c.value}</div>
          <div className="aj-stat-sub">{c.sub}</div>
        </div>
      ))}
    </div>
  );
};

export default function CSRScoresTab({ csrList }) {
  // Sort by converted_count descending so rank order is correct
  const [scores, setScores] = useState(
    [...DUMMY_SCORES].sort((a, b) => (b.converted_count ?? 0) - (a.converted_count ?? 0))
  );
  const [editNotes, setEditNotes] = useState({});  // { [csr_user_id]: string }
  const [unsaved, setUnsaved]     = useState({});  // { [csr_user_id]: true }
  const [saving, setSaving]       = useState(false);
  const [periodRange, setPeriodRange] = useState([dayjs().startOf('month'), dayjs()]);

  const handleNoteChange = (id, val) => {
    setEditNotes(prev => ({ ...prev, [id]: val }));
    setUnsaved(prev => ({ ...prev, [id]: true }));
  };

  const saveAll = async () => {
    if (!Object.keys(unsaved).length) { message.info('No unsaved changes.'); return; }
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));
    setScores(prev => prev.map(s => {
      const note = editNotes[s.csr_user_id];
      return note !== undefined ? { ...s, notes: note } : s;
    }));
    setUnsaved({});
    setSaving(false);
    message.success(`Saved ${Object.keys(unsaved).length} note(s)`);
  };

  const handleExport = () => {
    const headers = ['Rank', 'CSR Name', 'Email', 'Score (Converted)', 'Assigned', 'Contacted', 'Converted', 'Hot', 'In Progress', 'Conversion %', 'Notes'];
    const rows = scores.map((s, i) => {
      const converted = s.converted_count ?? 0;
      const convPct = s.assigned_count > 0
        ? ((converted / s.assigned_count) * 100).toFixed(1)
        : 0;
      return [
        i + 1, s.name, s.email, converted,
        s.assigned_count, s.contacted_count, converted,
        s.hot_count, s.in_progress_count, convPct,
        editNotes[s.csr_user_id] ?? s.notes,
      ].map(c => `"${String(c).replace(/"/g, '""')}"`);
    });
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = `csr_scores_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    message.success('Scores exported');
  };

  const columns = [
    {
      title: 'Rank', width: 60, align: 'center',
      render: (_, __, i) => (
        <span className={`aj-rank-badge ${getRankCls(i)}`}>{getRankLabel(i)}</span>
      )
    },
    {
      title: 'CSR', width: 200,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 700, color: '#1a1a2e' }}>{r.name}</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>{r.email}</div>
        </div>
      )
    },
    {
      // Score = converted_count
      title: (
        <span>
          Score
          <Tooltip title="Score = number of sessions successfully converted">
            <InfoCircleOutlined style={{ color: '#94a3b8', marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      width: 90, align: 'center',
      render: (_, r) => {
        const count = r.converted_count ?? 0;
        return <div className={`aj-score-badge ${getScoreCls(count)}`}>{count}</div>;
      }
    },
    {
      title: 'Assigned', dataIndex: 'assigned_count', width: 85, align: 'center',
      render: v => <Tag style={{ fontWeight: 700 }}>{v}</Tag>
    },
    {
      title: 'Contacted', dataIndex: 'contacted_count', width: 90, align: 'center',
      render: v => <Tag color="cyan" style={{ fontWeight: 700 }}>{v}</Tag>
    },
    {
      title: 'Converted', dataIndex: 'converted_count', width: 90, align: 'center',
      render: v => <Tag color="purple" style={{ fontWeight: 700 }}>{v ?? 0}</Tag>
    },
    {
      title: 'Hot', dataIndex: 'hot_count', width: 65, align: 'center',
      render: v => <Tag color="red" style={{ fontWeight: 700 }}>{v}</Tag>
    },
    {
      title: 'In Progress', dataIndex: 'in_progress_count', width: 90, align: 'center',
      render: v => <Tag color="orange" style={{ fontWeight: 700 }}>{v}</Tag>
    },
    {
      title: (
        <span>
          Conversion
          <Tooltip title="Converted ÷ Assigned">
            <InfoCircleOutlined style={{ color: '#94a3b8', marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      width: 120, align: 'center',
      render: (_, r) => {
        const converted = r.converted_count ?? 0;
        const pct = r.assigned_count > 0
          ? parseFloat(((converted / r.assigned_count) * 100).toFixed(1))
          : 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Progress percent={pct} size="small" showInfo={false}
              strokeColor="#6d28d9" style={{ flex: 1 }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: '#6d28d9', whiteSpace: 'nowrap' }}>
              {pct}%
            </span>
          </div>
        );
      }
    },
    {
      title: 'Notes', width: 220,
      render: (_, r) => (
        <Input.TextArea
          size="small"
          rows={1}
          autoSize={{ minRows: 1, maxRows: 3 }}
          placeholder="Add admin note…"
          value={editNotes[r.csr_user_id] ?? r.notes}
          onChange={e => handleNoteChange(r.csr_user_id, e.target.value)}
          style={{ borderRadius: 6, borderColor: unsaved[r.csr_user_id] ? '#b45309' : '#e2e8f0', fontSize: 12 }}
        />
      )
    },
  ];

  const unsavedCount = Object.keys(unsaved).length;

  return (
    <>
      <ScoreSummary scores={scores} />

      {/* Period bar */}
      <div className="aj-period-bar">
        <TeamOutlined style={{ color: THEME }} />
        <span style={{ fontWeight: 600, color: '#1a1a2e', fontSize: 13 }}>Period:</span>
        <RangePicker
          value={periodRange}
          onChange={setPeriodRange}
          format="DD MMM YYYY"
          allowClear={false}
          style={{ width: 280 }}
        />
        <div style={{ flex: 1 }} />
        {unsavedCount > 0 && (
          <span style={{ fontSize: 12, color: '#b45309', fontWeight: 600 }}>
            ● {unsavedCount} unsaved change{unsavedCount > 1 ? 's' : ''}
          </span>
        )}
        <Button
          icon={<SaveOutlined />}
          type="primary"
          loading={saving}
          onClick={saveAll}
          style={{ background: THEME, borderColor: THEME, borderRadius: 8, fontWeight: 600 }}
        >
          Save Notes
        </Button>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
          style={{ borderRadius: 8, borderColor: THEME, color: THEME }}
        >
          Export
        </Button>
      </div>

      {/* Table */}
      <div className="aj-table-card">
        <Table
          dataSource={scores}
          columns={columns}
          rowKey="csr_user_id"
          size="middle"
          scroll={{ x: 1100 }}
          pagination={false}
          locale={{ emptyText: (
            <div className="aj-empty">
              <TeamOutlined className="aj-empty-icon" />
              <div>No CSR scores for this period</div>
            </div>
          )}}
        />
      </div>
    </>
  );
}
